import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'
import { getUsersCards } from 'services/withDraw'

export default function useGetCards(
    id: undefined | number,
    userId: number | undefined,
    setSelectedBankNumber: React.Dispatch<React.SetStateAction<number>>,
    /**
     * Which of the member's own accounts to open on, when the caller knows
     * — the account they picked while filing their loan request. Falls back
     * to `is_default` when it is absent or no longer one of their accounts,
     * so every existing caller keeps exactly today's behaviour.
     *
     * Deliberately NOT routed through the `state.selectedCardNumber` branch
     * below: that one treats every account in the response as the member's,
     * including the manager's own.
     */
    preferredCardId?: number
) {
    const [activeAccount, setActiveAccount] = useState<
        IBankAccountCards | undefined
    >()
    const [activeAccounts, setActiveAccounts] = useState<
        IBankAccountCards[] | undefined
    >()
    const [otherAccounts, setOtherAccounts] = useState<
        IBankAccountCards[] | undefined
    >()
    const [reallySelectedCard, setReallySelectedCard] = useState<
        IBankAccountCards | undefined
    >()
    const { state } = useLocation()
    // console.log('state', state)
    const { data, isLoading } = useQuery(
        ['init-payment-os-wallet-cards', id],
        getUsersCards
    )
    useEffect(() => {
        // `receivers` can come back empty (a member with no registered
        // account), and indexing [0] blind threw «باگ خوردیم» over the whole
        // screen rather than just showing no accounts to pick from
        const accounts = data?.receivers?.[0]?.bankaccounts
        if (!isLoading && accounts) {
            // only honour the preference if that account is still one of
            // theirs; a card removed since the request must not leave the
            // step with nothing selected
            const preferred = accounts.some(
                (item) => item.id === preferredCardId && item.member === userId
            )
                ? preferredCardId
                : undefined
            const isChosen = (item: IBankAccountCards) =>
                preferred ? item.id === preferred : item.is_default
            const findUser = accounts.find(
                (item) => item.member === userId && isChosen(item)
            )
            findUser && setSelectedBankNumber(findUser.id)
            setActiveAccount(findUser)
            const findActiveAccounts: IBankAccountCards[] = []
            const findOtherAccounts: IBankAccountCards[] = []
            accounts.map((item) => {
                if (state?.selectedCardNumber) {
                    if (item.id === state.selectedCardNumber) {
                        findActiveAccounts.push({
                            ...item,
                            isSelected: true,
                        })
                        setReallySelectedCard({ ...item, isSelected: true })
                    } else {
                        findActiveAccounts.push({
                            ...item,
                            isSelected: false,
                        })
                    }
                } else {
                    if (item.member === userId) {
                        if (isChosen(item)) {
                            findActiveAccounts.push({
                                ...item,
                                isSelected: true,
                            })
                            setReallySelectedCard({ ...item, isSelected: true })
                        } else {
                            findActiveAccounts.push({
                                ...item,
                                isSelected: false,
                            })
                        }
                    } else {
                        findOtherAccounts.push({ ...item, isSelected: false })
                    }
                }
            })
            setActiveAccounts(findActiveAccounts)
            setOtherAccounts(findOtherAccounts)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, preferredCardId])
    const handleSelectCard = (id: number) => {
        const findActiveAccounts: IBankAccountCards[] = []
        const findOtherAccounts: IBankAccountCards[] = []
        data?.receivers?.[0]?.bankaccounts.map((item) => {
                if (item.member === userId) {
                    if (item.id === id) {
                        findActiveAccounts.push({ ...item, isSelected: true })
                        setReallySelectedCard({ ...item, isSelected: true })
                    } else {
                        findActiveAccounts.push({ ...item, isSelected: false })
                    }
                } else {
                    if (item.id === id) {
                        findOtherAccounts.push({ ...item, isSelected: true })
                        setReallySelectedCard({ ...item, isSelected: true })
                    } else {
                        findOtherAccounts.push({ ...item, isSelected: false })
                    }
                }
            })
        setActiveAccounts(findActiveAccounts)
        setOtherAccounts(findOtherAccounts)
    }
    return {
        data,
        isLoading,
        activeAccount,
        activeAccounts,
        otherAccounts,
        handleSelectCard,
        reallySelectedCard,
    }
}
